// discounts.ts — Discount cascade (book REGLES_DEVIS §5).
//
// Strict order: component base price → tier discount → client discount (multiplicative composition,
// 60 % cap) → general discount (hits everything, including forced prices and minima when the box is
// ticked) → rush last of all (outside the discount, low floor per flat fee).
//
// Ported from compute() of prototype v13.7.

import { num, round2 } from "../core/core";
import { engineSettings } from "../../data";
import type { RushOpt } from "../../models/data.model";

// AGENCY discount: entry range + presets (book 4.6)
// The agency discount (first in the cascade) is entered within a bounded range on the UI side. That is
// entry validation, NOT an engine limit: compute() can compose any rate (a quote migrated at 25 %
// computes correctly; only ENTRY above the maximum is refused). The maximum and the presets are
// settings (`quote_engine.agency_discount`).

/**
 * Highest agency discount accepted at entry, in percent (setting).
 * @returns The maximum percentage.
 */
export function agencyDiscountPctMax(): number {
  return engineSettings().agencyDiscount.maxPct;
}

/** An agency discount preset: prefills the field; `labelKey` is the i18n key of its name. */
export interface AgencyPreset {
  code: string;
  labelKey: string;
  pct: number;
}

/**
 * Agency discount presets (prefill the field; become client-record shortcuts in block 4), from the settings.
 * @returns The presets in menu order.
 */
export function agencyPresets(): readonly AgencyPreset[] {
  return engineSettings().agencyDiscount.presets.map((p) => ({
    code: p.code,
    labelKey: `engineAgencyPreset${p.code.charAt(0).toUpperCase()}${p.code.slice(1)}`,
    pct: p.pct,
  }));
}

/**
 * Is the agency discount entry (in %) within the allowed range (0 to the configured maximum)? UI validation.
 * @param pct The entered percentage.
 * @returns True when the entry is within the range.
 */
export function isAgencyDiscountValidPct(pct: number): boolean {
  return Number.isFinite(pct) && pct >= 0 && pct <= agencyDiscountPctMax();
}

/**
 * Clamps an agency discount entry (in %) into the allowed range (anything beyond sticks to the bound).
 * @param pct The entered percentage.
 * @returns The clamped percentage (0 when not finite).
 */
export function clampAgencyDiscountPct(pct: number): number {
  if (!Number.isFinite(pct)) return 0;
  return Math.max(0, Math.min(agencyDiscountPctMax(), pct));
}

/**
 * TOTAL discount of a line: multiplicative composition of the volume tier and the client discount,
 * CAPPED (margin floor, absolute commercial policy: setting `discountCapRate`, 60 % at seed).
 * r = min(cap; 1 − (1 − tier) × (1 − client)). E.g. tier 10 % + client 15 % → 23.5 %.
 * @param tierRate The volume tier rate (0..1).
 * @param clientDiscount The client discount rate (0..1).
 * @returns The composed discount rate, capped at the configured rate.
 */
export function lineDiscount(tierRate: number, clientDiscount: number): number {
  return Math.min(engineSettings().parameters.discountCapRate, 1 - (1 - tierRate) * (1 - clientDiscount));
}

/**
 * Normalised general discount rate: entry in %, clamped 0–100 → 0–1 (the total never goes negative).
 * @param input The entered percentage.
 * @returns The rate between 0 and 1.
 */
export function generalDiscountRate(input: unknown): number {
  return Math.max(0, Math.min(100, num(input))) / 100;
}

/**
 * A line as seen by the general discount (box unticked): its total and its minimum unit price floor.
 * Minimal structural view — compute() supplies these fields from the line calculation.
 */
export interface LineFloor {
  /** Line total excl. VAT. */
  total: number;
  /** Total minimum unit price floor of the line (0 or absent when no protected floor). */
  floorTotal?: number;
  /** Line carried by the material minimum flat fee (excluded from the discount base). */
  flatFeeReplaced?: boolean;
}

/**
 * General discount amount (book §5, "apply discount to minimum flat fee and minimum unit price" box).
 *
 * - TICKED (`includeMinima`): the discount bites on EVERYTHING (flat fees and minimum unit prices
 *   included). Lines already sit at their floor, so the discount applies globally → totalBefore × rate.
 * - UNTICKED (default, protective): line by line, the minimum unit price stays impassable — clamp to
 *   the floor, then discount only the surplus. Services are discounted at full rate.
 * @param params The rate, the bases, the priced lines and the minima flag.
 * @returns The discount amount, rounded to the cent.
 */
export function generalDiscountAmount(params: {
  /** Normalised rate (0–1), from generalDiscountRate. */
  rate: number;
  /** Total base: print + services. */
  totalBefore: number;
  /** Services total (discounted at full rate in protected mode). */
  servicesTotal: number;
  /** Priced print lines. */
  lines: readonly LineFloor[];
  includeMinima: boolean;
}): number {
  const { rate, totalBefore, servicesTotal, lines, includeMinima } = params;
  if (rate <= 0) return 0;
  if (includeMinima) return round2(totalBefore * rate);

  let discount = 0;
  for (const r of lines) {
    if (r.flatFeeReplaced || !(r.total > 0)) continue; // carried by the minimum flat fee → outside the base
    const floor = r.floorTotal ?? 0;
    if (floor > 0) {
      const discounted = r.total * (1 - rate);
      // clamp to the floor (bounded by the line total) then discount only the surplus
      discount += r.total - Math.max(discounted, Math.min(floor, r.total));
    } else {
      discount += r.total * rate; // line without a floor: full rate
    }
  }
  discount += servicesTotal * rate; // services: full rate
  return round2(discount);
}

/** Rush result: amount, label, PA (workshop cost, 0 by default). */
export interface RushResult {
  amount: number;
  label: string;
  pa: number;
}

/**
 * Rush (book §11): applied LAST on the already-discounted base, and not subject to the general discount.
 * Priced flat fee: max(floor, discounted base × pct). Negotiated flat fee: free entered amount.
 * Rush PA = 0 by default (pure UPSIZE margin), entered when a workshop bills an extra.
 * @param opt The selected rush option, if any.
 * @param discountedBase The base after the general discount.
 * @param rushNego The negotiated amount entered by hand.
 * @param rushPa The workshop cost entered by hand.
 * @returns The rush amount, label and PA.
 */
export function rush(
  opt: RushOpt | undefined,
  discountedBase: number,
  rushNego: unknown,
  rushPa: unknown,
): RushResult {
  if (!opt || !opt.code) return { amount: 0, label: "", pa: 0 };
  const amount = opt.is_negotiated
    ? Math.max(0, num(rushNego))
    : Math.max(opt.floor_amount ?? 0, discountedBase * (opt.pct ?? 0));
  return { amount: round2(amount), label: opt.label, pa: round2(Math.max(0, num(rushPa))) };
}
